import { type ITransaction, Query } from "@gen/app-query";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { HeadingContainer } from "~/components/heading_container";
import { useCanExecuteOperation } from "~/contexts/auth_query";

interface UserDetailsProps {
  transactionData: ITransaction;
  clientId: string;
  userTransactionRole: "buyer" | "seller";
}

export function UserDetails({
  transactionData,
  clientId,
  userTransactionRole,
}: UserDetailsProps) {
  const canExecuteOperation = useCanExecuteOperation();

  const userId =
    userTransactionRole === "buyer"
      ? transactionData.buyer_id
      : transactionData.seller_id;

  const { data: userData } = Query.useCore_getClientUserQuery(
    clientId,
    userId!,
    {
      enabled:
        canExecuteOperation("core.getClientUser") && !!clientId && !!userId,
    },
  );

  const email = userData?.email;

  const isGuest =
    userTransactionRole === "buyer"
      ? transactionData?.buyer_is_guest
      : transactionData?.seller_is_guest;

  return (
    <Box sx={{ mb: 5 }}>
      <HeadingContainer title="Contact" variant="h6" borderType="dashed" />
      <Stack spacing={1}>
        <Stack spacing={0.5}>
          {email && isGuest && (
            <Stack flexDirection="row" alignItems="center">
              <Typography variant="body2">Guest:&nbsp;</Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                data-testid={`${userTransactionRole}-guest-email-text`}
              >
                {email}
              </Typography>
            </Stack>
          )}
          {email && !isGuest && (
            <Stack flexDirection="row" alignItems="center">
              <Typography variant="body2">Full:&nbsp;</Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                data-testid={`${userTransactionRole}-email-text`}
              >
                {email}
              </Typography>
            </Stack>
          )}
          {!email && userId && (
            <Stack flexDirection="row" alignItems="center">
              <Typography variant="body2">User ID:&nbsp;</Typography>
              <Typography
                variant="body2"
                fontWeight="bold"
                data-testid={`${userTransactionRole}-user-id-text`}
              >
                {userId}
              </Typography>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  );
}
